import type { AnalyticsDestination, AnalyticsTraits, EventProperties } from "@/lib/analytics/analytics.types";
import { isSentinelValue } from "@/lib/env";
import type { OkAny } from "@/types/OkAny.type";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;

const enabled = (): boolean =>
  typeof window !== "undefined" &&
  !!MEASUREMENT_ID &&
  !isSentinelValue(MEASUREMENT_ID) &&
  process.env.NODE_ENV !== "test";

/**
 * Thin gtag() wrapper that seeds a proper gtag stub when the google-tags-init
 * script hasn't fired yet (strategy="afterInteractive" may lose the race with
 * React useEffect on first mount). The stub uses a regular `function` (not an
 * arrow function) so `arguments` is an Arguments object — the same format that
 * gtag.js expects when dequeuing from dataLayer. The real init script will
 * overwrite window.gtag and window.dataLayer = window.dataLayer || [] will
 * preserve the already-queued entries.
 */
const gtag = (...args: unknown[]): void => {
  if (!enabled()) return;
  if (typeof window.gtag !== "function") {
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function () {
      // biome-ignore lint/complexity/noArguments: `arguments` is intentional — we need an Arguments object (not a plain array) for gtag.js queue compatibility
      (window.dataLayer as OkAny[]).push(arguments);
    } as (...a: unknown[]) => void;
  }
  window.gtag(...args);
};

export const ga4Destination: AnalyticsDestination = {
  name: "ga4",
  isEnabled: enabled,
  pageView: (path) => {
    gtag("event", "page_view", {
      page_location: typeof window !== "undefined" ? `${window.location.origin}${path}` : path,
      send_to: MEASUREMENT_ID,
    });
  },
  track: (name, properties?: EventProperties) => {
    gtag("event", name, {
      ...(properties ?? {}),
      send_to: MEASUREMENT_ID,
    });
  },
  identify: (traits: AnalyticsTraits) => {
    // Do not set GA4 user_properties with email/id (PII in reports).
    // user_data is the enhanced-conversions channel: gtag hashes with SHA-256.
    // Send plaintext only — never pre-hash (double-hashing breaks match rates).
    // Omit unused keys rather than sending blanks.
    if (typeof traits.email === "string" && traits.email.trim() !== "") {
      // Normalize before gtag hashes: trim + lowercase (Google EC match key).
      gtag("set", "user_data", { email: traits.email.trim().toLowerCase() });
    }
  },
  reset: () => {
    // Clear enhanced-conversions user_data so a later session cannot reuse
    // the previous hashed email. Session/cookie lifecycle stays with gtag.
    gtag("set", "user_data", {});
  },
};
